/**
 * Recherche d'adresses du site public (donoor.org) et du tunnel boutique.
 *
 * DEUX PORTES (décision de Karim du 28/09/2026 au soir, contrat §18.33) :
 *  · route PROTÉGÉE de l'API (`/api/adresses/public/*`) quand l'interrupteur
 *    `NUXT_PUBLIC_ADRESSES_PUBLIQUES` est allumé et qu'une sitekey Turnstile
 *    existe : Google Places d'abord (fautes de frappe, lieux nommés, monde
 *    entier), puis BAN / OpenStreetMap en secours — la même réponse que le
 *    dashboard. Anti-robots : Cloudflare Turnstile invisible, vérifié UNE
 *    fois par session (30 min, `creerSessionAdressesPubliques`), limites par
 *    IP et par session, part de budget du site public ; sans session valide,
 *    le serveur répond par la BAN seule, jamais Google ;
 *  · BAN DIRECTE depuis le navigateur (Géoplateforme de l'IGN) : interrupteur
 *    éteint, ou API injoignable (404 pendant un déploiement, 429, 5xx,
 *    réseau). L'écran ne reste jamais sans suggestion.
 *
 * Adresses PERSONNELLES (livraison, participants, adhérents, reçu après un
 * don) : le serveur force le mode privé (ni cache écrit, ni journal) ; le
 * formulaire n'envoie que le TEXTE à l'enregistrement, le serveur le redécoupe.
 *
 * Hôte BAN (lot 4, 2026-09-27) : `api-adresse.data.gouv.fr` répond
 * `Deprecation` et `Sunset` depuis le 31/01/2026 ; on appelle directement la
 * Géoplateforme (connect-src de la CSP : data.geopf.fr).
 *
 * Parseur UNIQUE `suggestionsDepuisReponseBan` (@donoor/utils), partagé avec
 * l'API, le portail donateur et le backup-manager.
 *
 * Jamais de throw : toute erreur donne une liste vide ou `null` (secours).
 */
import {
  BAN_URL_RECHERCHE,
  estJetonAdressesPubliques,
  requeteRecherche,
  rueDepuisSuggestion,
  SCORE_BAN_SUGGESTION,
  suggestionsDepuisReponseBan,
  type CodeTerritoire,
  type PrecisionAdresse,
} from "@donoor/utils";
import type {
  AdresseStructuree,
  AttributionSuggestions,
  ReponseSessionAdressesPubliques,
  SuggestionAdresse,
} from "@donoor/types/adresse";

/** Nombre de suggestions affichées (inchangé). */
const LIMITE_SUGGESTIONS = 5;

export interface AddressSuggestion {
  label: string;
  value: string;
  /**
   * Morceaux structurés (2026-09-26, parité dashboard). La BAN les fournit :
   * l'adresse de livraison de la boutique est stockée en rue / code postal /
   * ville SÉPARÉS, et redécouper le libellé à la main est une source de bugs.
   * Facultatifs : les écrans qui ne lisent que `label`/`value` sont intacts.
   *
   * `street` = rue de la suggestion, règle partagée `rueDepuisSuggestion`
   * (@donoor/utils, identique au dashboard et au mobile) : la rue BAN
   * (« 12 Rue de Rivoli »), sinon la ligne principale (le lieu-dit), le
   * libellé seulement en dernier recours. Pour la BAN, c'est exactement le
   * nom de la ligne, comme avant : le tunnel boutique en dépend.
   */
  street?: string;
  postcode?: string;
  city?: string;
  /**
   * Enrichissements du lot 4 (facultatifs, AJOUTS seulement) : identifiant
   * stable de la BAN (`properties.id`, clé de liste), code INSEE, territoire
   * d'outre-mer, point et précision. Aucun écran existant ne les exige.
   */
  idBan?: string;
  codeInsee?: string;
  /** Pays ISO (FR pour tout l'outre-mer). */
  pays?: string;
  territoire?: CodeTerritoire;
  latitude?: number;
  longitude?: number;
  precision?: PrecisionAdresse;
}

/**
 * Suggestion (BAN, OpenStreetMap ou Google) → ce que les formulaires du site
 * reçoivent par `select`. `adresse` : l'adresse COMPLÈTE (BAN / OSM : celle de
 * la suggestion ; Google : celle que `/details` a vérifiée). Le libellé est
 * celui de l'adresse vérifiée quand il existe ; la rue suit la règle partagée
 * `rueDepuisSuggestion` — jamais le NOM d'un lieu Google (§18.23).
 */
export function versAddressSuggestion(s: SuggestionAdresse, adresse: AdresseStructuree | null = s.adresse): AddressSuggestion {
  const libelle = adresse?.libelle || s.libelle;
  return {
    label: libelle,
    value: libelle,
    street: rueDepuisSuggestion(s, adresse ? { libelle: adresse.libelle, adresse } : null),
    ...(adresse ? morceauxFacultatifs(adresse) : {}),
  };
}

/** Morceaux facultatifs d'une adresse complète (clés absentes quand ils manquent). */
function morceauxFacultatifs(a: AdresseStructuree): Partial<AddressSuggestion> {
  const m: Partial<AddressSuggestion> = {};
  if (a.codePostal) m.postcode = a.codePostal;
  if (a.ville) m.city = a.ville;
  if (a.idBan) m.idBan = a.idBan;
  if (a.codeInsee) m.codeInsee = a.codeInsee;
  if (a.pays) m.pays = a.pays;
  if (a.territoire) m.territoire = a.territoire;
  if (a.latitude != null && a.longitude != null) {
    m.latitude = a.latitude;
    m.longitude = a.longitude;
  }
  if (a.precision) m.precision = a.precision;
  return m;
}

/** Réponse brute de la Géoplateforme → suggestions complètes (parseur unique). */
export function suggestionsBanDirecte(json: unknown): SuggestionAdresse[] {
  return suggestionsDepuisReponseBan(json, { scoreMin: SCORE_BAN_SUGGESTION, limite: LIMITE_SUGGESTIONS });
}

/**
 * Réponse brute de la Géoplateforme → suggestions du site. Exportée pour les
 * tests (mêmes réponses réelles que les fixtures de @donoor/utils).
 */
export function suggestionsDepuisBan(json: unknown): AddressSuggestion[] {
  return suggestionsBanDirecte(json).map((s) => versAddressSuggestion(s));
}

/** Délai maximal d'un appel à l'API (au-dessus de l'échéance serveur de 5 s). */
const DELAI_API_MS = 6000;

/** Une suggestion lisible : garde-fou contre une réponse inattendue (fenêtre de déploiement). */
function estSuggestion(s: unknown): s is SuggestionAdresse {
  const x = s as Partial<SuggestionAdresse> | null;
  return (
    !!x &&
    typeof x.id === "string" &&
    typeof x.libelle === "string" &&
    x.libelle.trim().length > 0 &&
    (x.fournisseur === "ban" || x.fournisseur === "osm" || x.fournisseur === "google")
  );
}

/** POST JSON vers l'API Donoor ; null sur toute erreur (réseau, délai, HTTP ≠ 2xx). */
async function publierJson(url: string, corps: Record<string, unknown>, signal?: AbortSignal): Promise<unknown | null> {
  const ctrl = new AbortController();
  const minuterie = setTimeout(() => ctrl.abort(), DELAI_API_MS);
  const relais = () => ctrl.abort();
  signal?.addEventListener("abort", relais);
  try {
    const reponse = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(corps),
      signal: ctrl.signal,
    });
    if (!reponse.ok) return null;
    return await reponse.json();
  } catch {
    return null;
  } finally {
    clearTimeout(minuterie);
    signal?.removeEventListener("abort", relais);
  }
}

/** Résultat d'une recherche par la route protégée ; `null` = passer à la BAN directe. */
export interface ResultatRecherchePublique {
  suggestions: SuggestionAdresse[];
  attribution: AttributionSuggestions | null;
  /** Le serveur n'a pas reconnu la session (expirée, révoquée) : l'oublier. */
  sessionRefusee: boolean;
}

export const addressLookupApi = {
  /**
   * Recherche BAN. Au plus 5 suggestions. Jamais de throw.
   *
   * Texte envoyé : règle partagée `requeteRecherche` (blancs repliés, 3 à
   * 200 caractères) — la même que l'API et le mobile. Un texte trop long
   * n'est JAMAIS tronqué : des suggestions sur un texte que personne n'a
   * tapé, différentes d'une plateforme à l'autre. Pas d'appel, pas de liste.
   */
  async search(query: string): Promise<AddressSuggestion[]> {
    const q = requeteRecherche(query);
    if (q === null) return [];
    try {
      const url = `${BAN_URL_RECHERCHE}?q=${encodeURIComponent(q)}&limit=${LIMITE_SUGGESTIONS}&autocomplete=1`;
      const response = await fetch(url);
      if (!response.ok) return [];
      return suggestionsDepuisBan(await response.json());
    } catch {
      return [];
    }
  },
};

/** Routes protégées de l'API (site public, portail donateur). */
export const adressesPubliquesApi = {
  /** Échange d'un jeton Turnstile contre une session publique (30 min). Jamais de throw. */
  async ouvrirSession(apiBase: string, turnstile: string): Promise<ReponseSessionAdressesPubliques> {
    const r = (await publierJson(`${apiBase}/api/adresses/public/session`, { turnstile })) as
      | Partial<ReponseSessionAdressesPubliques>
      | null;
    return estJetonAdressesPubliques(r?.jeton)
      ? { jeton: r!.jeton!, expireDans: Number(r!.expireDans) || 0 }
      : { jeton: null, expireDans: 0 };
  },

  /**
   * Suggestions : Google d'abord (session valide), secours BAN / OSM côté
   * serveur ; `null` si l'API ne répond pas (le champ passe à la BAN directe).
   */
  async rechercher(
    apiBase: string,
    q: string,
    session: string,
    { jeton, pays, signal }: { jeton: string | null; pays?: string | null; signal?: AbortSignal },
  ): Promise<ResultatRecherchePublique | null> {
    const texte = requeteRecherche(q);
    if (texte === null) return { suggestions: [], attribution: null, sessionRefusee: false };
    const corps: Record<string, unknown> = { q: texte, session };
    if (jeton) corps.jeton = jeton;
    if (pays) corps.pays = pays;
    const r = (await publierJson(`${apiBase}/api/adresses/public/suggestions`, corps, signal)) as {
      suggestions?: unknown;
      attribution?: unknown;
      degrade?: unknown;
    } | null;
    if (!r) return null;
    const suggestions = Array.isArray(r.suggestions) ? r.suggestions.filter(estSuggestion) : [];
    const attribution =
      r.attribution === "google" || r.attribution === "osm"
        ? r.attribution
        : suggestions.some((x) => x.fournisseur === "google")
          ? "google"
          : suggestions.some((x) => x.fournisseur === "osm")
            ? "osm"
            : null;
    return { suggestions, attribution, sessionRefusee: !!jeton && r.degrade === "sans_acces" };
  },

  /** Adresse vérifiée d'une suggestion Google ; null en cas d'échec (le champ garde le texte tapé). */
  async details(
    apiBase: string,
    placeId: string,
    session: string,
    jeton: string | null,
    signal?: AbortSignal,
  ): Promise<AdresseStructuree | null> {
    const r = (await publierJson(`${apiBase}/api/adresses/public/details`, { placeId, session, jeton }, signal)) as {
      adresse?: AdresseStructuree | null;
    } | null;
    const a = r?.adresse;
    return a && typeof a.libelle === "string" && a.libelle.trim() ? a : null;
  },

  /** BAN directe (secours navigateur) : suggestions complètes, jamais de throw. */
  async banDirecte(q: string): Promise<SuggestionAdresse[]> {
    const texte = requeteRecherche(q);
    if (texte === null) return [];
    try {
      const url = `${BAN_URL_RECHERCHE}?q=${encodeURIComponent(texte)}&limit=${LIMITE_SUGGESTIONS}&autocomplete=1`;
      const reponse = await fetch(url);
      if (!reponse.ok) return [];
      return suggestionsBanDirecte(await reponse.json());
    } catch {
      return [];
    }
  },
};
